import React, { useState } from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaFilter, FaUndo } from "react-icons/fa";
import AppButton from "../../../../shared/AppButton";
import CustomFilterInput from "../../../../shared/CustomFilterInput";

const DEFAULT_LIMIT = 15;

const stripEmpty = (obj) =>
  Object.keys(obj).reduce((acc, key) => {
    const value = obj[key];
    if (value !== "" && value !== null && value !== undefined) acc[key] = value;
    return acc;
  }, {});

const toFilterInput = (values, limit = DEFAULT_LIMIT, page = 1) => ({
  from_date: values.fromDate,
  to_date: values.toDate,
  payer_id: values.payerId,
  batch_id: values.batchID,
  document_id: values.documentId,
  tp_ref_no: values.tpRefNo,
  type: values.type,
  status: "Ready for submission",
  limit,
  page,
});

function Filter({
  refetch,
  setPageNo,
  setFromDate,
  setToDate,
  setLoading,
  setRefetchFilterVariables,
  pageNo,
  limit,
  setSubmitClaimData,
  setTotalPages,
  setTotalSubmits,
  setSelectedValues,
}) {
  const { t } = useTranslation();
  const [payers, setPayers] = useState([]);
  const [isApplying, setIsApplying] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const currentDate = new Date();
  const navigate = useNavigate();
  const defaultFromDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-01`;
  const defaultToDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0,
  ).getDate()}`;

  const types = [
    { id: "institutional", name: t("Institutional") },
    { id: "oral", name: t("Dental") },
    { id: "pharmacy", name: t("Pharmacy") },
    { id: "professional", name: t("Professional") },
    { id: "vision", name: t("Optical") },
  ];

  const getPersistedFilters = () => {
    try {
      const savedFilters = localStorage.getItem("submitClaimsFilters");
      if (savedFilters) {
        return JSON.parse(savedFilters);
      }
    } catch (error) {
      console.error("Error loading persisted filters:", error);
    }
    return null;
  };

  const getPersistedPagination = () => {
    try {
      const savedPagination = localStorage.getItem("submitClaimsPagination");
      if (savedPagination) {
        return JSON.parse(savedPagination);
      }
    } catch (error) {
      console.error("Error loading persisted pagination:", error);
    }
    return null;
  };

  const persistedFilters = getPersistedFilters();
  const persistedPagination = getPersistedPagination();

  const addData = async (values, customPage = null) => {
    const targetPage = customPage !== null ? customPage : 1;
    const nextLimit = limit || DEFAULT_LIMIT;
    setPageNo(targetPage);
    setSelectedValues([]);
    setFromDate(values.fromDate);
    setToDate(values.toDate);

    localStorage.setItem("submitClaimsFilters", JSON.stringify(values));
    localStorage.setItem(
      "submitClaimsPagination",
      JSON.stringify({
        pageNo: targetPage,
        limit: nextLimit,
      }),
    );

    const obj = toFilterInput(values, nextLimit, targetPage);
    const cleanObj = stripEmpty(obj);
    try {
      setLoading(true);
      setIsApplying(true);
      await refetch({ input: cleanObj }).then((res) => {
        if (res?.data?.listPayerClaims?.data) {
          setSubmitClaimData(res.data.listPayerClaims.data);
          setTotalPages(res.data.listPayerClaims.pages);
          setTotalSubmits(res.data.listPayerClaims.total);
        }
      });
    } finally {
      setLoading(false);
      setIsApplying(false);
    }
    setRefetchFilterVariables(cleanObj);
  };

  const handleResetForm = async () => {
    const nextLimit = limit || DEFAULT_LIMIT;
    const resetValues = {
      fromDate: defaultFromDate,
      toDate: defaultToDate,
      payerId: "",
      batchID: "",
      documentId: "",
      tpRefNo: "",
      type: "",
    };
    setPageNo(1);
    setSelectedValues([]);
    setFromDate(resetValues.fromDate);
    setToDate(resetValues.toDate);

    localStorage.removeItem("submitClaimsFilters");
    localStorage.setItem(
      "submitClaimsPagination",
      JSON.stringify({
        pageNo: 1,
        limit: nextLimit,
      }),
    );
    resetForm({ values: resetValues });

    const obj = toFilterInput(resetValues, nextLimit, 1);
    const cleanObj = stripEmpty(obj);
    try {
      setLoading(true);
      setIsResetting(true);
      await refetch({ input: cleanObj }).then((res) => {
        if (res?.data?.listPayerClaims?.data) {
          setSubmitClaimData(res.data.listPayerClaims.data);
          setTotalPages(res.data.listPayerClaims.pages);
          setTotalSubmits(res.data.listPayerClaims.total);
        }
      });
    } finally {
      setLoading(false);
      setIsResetting(false);
    }
    setRefetchFilterVariables(cleanObj);
  };

  React.useEffect(() => {
    const payersData = JSON.parse(localStorage.getItem("payersData"));
    if (payersData && payersData.length > 0) {
      setPayers(payersData);
    } else {
      navigate("/dashboard");
    }
  }, []);

  React.useEffect(() => {
    if (persistedFilters && persistedPagination) {
      addData(persistedFilters, persistedPagination.pageNo);
    } else if (persistedFilters) {
      addData(persistedFilters, 1);
    }
  }, []);

  const { values, handleChange, handleBlur, handleSubmit, resetForm } =
    useFormik({
      initialValues: {
        fromDate:
          persistedFilters?.fromDate ||
          defaultFromDate,
        toDate: persistedFilters?.toDate || defaultToDate,
        payerId: persistedFilters?.payerId || "",
        batchID: persistedFilters?.batchID || "",
        documentId: persistedFilters?.documentId || "",
        tpRefNo: persistedFilters?.tpRefNo || "",
        type: persistedFilters?.type || "",
      },
      onSubmit: (values) => addData(values),
    });

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-end justify-start gap-2 w-full">
        <CustomFilterInput
          label={t("From Date")}
          type="date"
          name="fromDate"
          id="fromDate"
          value={values.fromDate}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("From Date")}
          className="w-full sm:w-auto flex-1 min-w-[130px] max-w-[180px]"
        />
        <CustomFilterInput
          label={t("To Date")}
          type="date"
          name="toDate"
          id="toDate"
          value={values.toDate}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("To Date")}
          className="w-full sm:w-auto flex-1 min-w-[130px] max-w-[180px]"
        />
        <CustomFilterInput
          className="w-full sm:w-auto flex-1 min-w-[150px] max-w-[200px]"
          label={t("Select Payer")}
          name="payerId"
          id="payerId"
          value={values.payerId}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Select Payer")}
          isSelect
          renderOptions={() =>
            payers.map((category) => (
              <optgroup
                key={category.id}
                label={
                  category?.name_en
                    ? `${category?.name_en} (${category?.license_id})`
                    : ""
                }
              >
                {category?.payers?.map((insurance, idx) => (
                  <option key={`${insurance.id}-${idx}`} value={insurance.id}>
                    {insurance.name_en} ({insurance?.license_id})
                  </option>
                ))}
              </optgroup>
            ))
          }
        />
        <CustomFilterInput
          label={t("Document ID")}
          type="text"
          name="documentId"
          id="documentId"
          value={values.documentId}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Search by Document ID")}
          className="w-full sm:w-auto flex-1 min-w-[140px] max-w-[180px]"
        />
        <CustomFilterInput
          label={t("Claim Reference")}
          type="text"
          name="tpRefNo"
          id="tpRefNo"
          value={values.tpRefNo}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Claim Reference")}
          className="w-full sm:w-auto flex-1 min-w-[140px] max-w-[180px]"
        />
        <CustomFilterInput
          className="w-full sm:w-auto flex-1 min-w-[130px] max-w-[180px]"
          label={t("Type")}
          name="type"
          id="type"
          value={values.type}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Select Type")}
          isSelect
          renderOptions={() =>
            types.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))
          }
        />

        <div className="flex justify-end gap-2 shrink-0">
          <AppButton
            variant="reset"
            onClick={handleResetForm}
            startIcon={<FaUndo className="!text-[12px]" />}
            isLoading={isResetting}
            loadingText=""
          />
          <AppButton
            onClick={handleSubmit}
            startIcon={<FaFilter className="!text-[12px]" />}
            isLoading={isApplying}
            loadingText=""
          />
        </div>
      </div>
    </div>
  );
}

export default React.memo(Filter);
